import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { 
  MedicalConsultation, 
  MedicalTest, 
  CartQuantityItem, 
  Order, 
  User, 
  Drug
} from '../../models';
import {
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  AuthenticationService,
  CartService,
  DataService,
  MedicalTestService,
  OrderService,
  UserProfileService
} from '../../services';
import { first, mergeMap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { CountryISO, SearchCountryField, PhoneNumberFormat } from 'ngx-intl-tel-input';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  providers: [DatePipe]
})
export class DashboardComponent implements OnInit {
  clientDashboardForm: FormGroup;
  submitted = false;
  user: User;
  isUser: boolean;
  isAdmin: boolean;
  orders: Order[] = [];
  incompleteOrder: Order;
  separateDialCode = false;
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [
    CountryISO.Uganda
  ];
  cartItems: (MedicalTest | Drug)[] = [];
  noCart: string;
  medicalTests: MedicalTest[] = [];
  consultationAmount: number | undefined;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private datePipe: DatePipe,
    private authenticationService: AuthenticationService,
    private dataService: DataService,
    private cartService: CartService,
    private medicalTestService: MedicalTestService,
    private orderService: OrderService,
    private userProfileService: UserProfileService
  ) { }

  ngOnInit(): void {
    this.medicalTestService.getMedicalTests()
      .pipe(
        mergeMap((medicalTests) => {
          this.medicalTests = medicalTests as MedicalTest[]; 
          this.dataService.saveAllLocalMedicalTests(this.medicalTests); 
          return this.medicalTestService.getLocalMedicalConsultationAmount(); 
        })
      )
      .subscribe((consultationAmount: MedicalConsultation) => {
          this.consultationAmount = consultationAmount.amount || 0; 
          this.dataService.saveLocalMedicalConsultationAmount(this.consultationAmount); 
        }
      );
    if (this.authenticationService.currentUser()) {
      this.user = this.authenticationService.currentUser()['user'];
      if (this.user) {
        this.clientDashboardForm = this.formBuilder.group({
          role: [this.user?.role],
          firstname: [this.user?.firstname, Validators.required ],
          lastname: [this.user?.lastname, Validators.required],
          email: [this.user?.email],
          telephone: [this.user?.telephone, Validators.required],
          birthDate: [this.datePipe.transform(this.user?.birth_date, 'longDate')],
          address: [this.user?.address, Validators.required],
        });   
        this.cartService.getUserCart(this.user.id).subscribe(response => {
          if (response.message !== 'No associated cart') {
            let backendCartItems: (MedicalTest | Drug)[] = [];
            const backendQuantities: number[] = response['quantity'] || [];
            const backendQuantityTypeIds: number[] = response['selected_quantity_type_ids'] || [];
            const backendHasMedicalTests = response['medical_tests'] && response['medical_tests'].length > 0;
            const backendHasDrugs = response['drugs'] && response['drugs'].length > 0;
            if (backendHasMedicalTests) {
              backendCartItems = response['medical_tests'] as MedicalTest[];
            } else if (backendHasDrugs) {
              backendCartItems = response['drugs'] as Drug[];
            }
            const localCartItems = JSON.parse(localStorage.getItem('cartItems') || '[]');
            const localHasDrugs = localCartItems.length > 0 && 'country_of_origin' in localCartItems[0];
            const localHasMedicalTests = localCartItems.length > 0 && !('country_of_origin' in localCartItems[0]);
            // Check if backend and local cart items are of different types
            if (
              (backendHasMedicalTests && localHasDrugs) ||
              (backendHasDrugs && localHasMedicalTests)
            ) {
              localCartItems.map((product: MedicalTest | Drug) => {
                this.cartService.removeFromCart(product);
              });
              const backendQuantitiesArray: CartQuantityItem[] = backendCartItems.map((item, index) => {
                const id = item.id;
                const quantity = backendQuantities[index] || 1;
                const quantityTypeId = Number(backendQuantityTypeIds[index]);
                let quantityTypeName = '';
                let amount = '0';
                if ('country_of_origin' in item) {
                  const drug = item as Drug;
                  if (Array.isArray(drug.quantity_types) && drug.quantity_types.length > 0) {
                    const selectedQuantityType = drug.quantity_types.find(q => Number(q.id) === quantityTypeId);
                    quantityTypeName = selectedQuantityType?.name || '';
                    amount = selectedQuantityType?.selling_price || '0';
                  }
                }
                return {
                  productId: id,
                  quantity: quantity,
                  quantity_type: quantityTypeName,
                  amount: amount
                };
              });
              this.cartService.saveToCart(backendCartItems);
              this.dataService.saveToLocalStorage('cartItems', backendCartItems);
              this.dataService.saveLocalCartItemQuantities(backendQuantitiesArray);
              this.dataService.cartUpdatedSubject.next();
              return;  
            }
            const mergedMap = new Map<number, CartQuantityItem>();
            backendCartItems.map((item, index) => {
              const id = item.id;
              const quantity = backendQuantities[index] || 1;
              const quantityTypeId = Number(backendQuantityTypeIds[index]); 
              let quantityTypeName = '';
              let amount = '0';
              if ('country_of_origin' in item) { 
                const drug = item as Drug;
                if (Array.isArray(drug.quantity_types) && drug.quantity_types.length > 0) {
                  const selectedQuantityType = drug.quantity_types.find(q => Number(q.id) === quantityTypeId);
                  quantityTypeName = selectedQuantityType?.name || '';
                  amount = selectedQuantityType?.selling_price || '0';
                }
              }
              mergedMap.set(id, {
                productId: id,
                quantity: quantity,
                quantity_type: quantityTypeName,
                amount: amount,
              });
            });
            const localQuantities = this.dataService.getExistingLocalCartItemQuantities();
            localQuantities.map(localItem => {
              mergedMap.set(localItem.productId, localItem);
            });
            const mergedQuantitiesArray: CartQuantityItem[] = Array.from(mergedMap.values());
            this.cartService.saveToCart(backendCartItems);
            this.dataService.saveLocalCartItemQuantities(mergedQuantitiesArray);
            this.dataService.saveToLocalStorage('cartItems', backendCartItems);
          } 
        });
      }
    } else {
      this.router.navigateByUrl('authenticate/login');
    }
    this.orderService.getMyOrders(this.user.id).subscribe(orders => {
      this.orders = (orders as Order[]).reverse();
    });
      
  }

  get f() {
    return this.clientDashboardForm.controls;
  }

  goToOrder() {
    this.incompleteOrder = this.orders.find(order => order.status !== 'Order Complete') as Order;
    if (!this.incompleteOrder) {
      Swal.fire({
        title: 'No Orders',
        html: 'You do not have any ongoing orders',
        icon: 'info',
        confirmButtonColor: '#00ACB1',
      });
    }
    this.dataService.saveLocalDataToView(this.incompleteOrder);
    this.router.navigate(['order-timeline']);
  }

  onViewDetails(order: Order) {
    this.dataService.saveLocalDataToView(order);
    this.router.navigate(['order-details']);
  }

  onSubmit() {
    const formData = {...this.clientDashboardForm.value};
    this.submitted = true;
    if (this.clientDashboardForm.invalid) {
      return;
    }
    formData.telephone = this.f.telephone.value.e164Number;
    delete formData.birthDate;
    const formInfo = new FormData();
    this.userProfileService.editUser(formData, this.user.id)
      .pipe(first()).subscribe(response => {
        this.handleResponse(true);
      },
        error => {
          this.handleResponse(false);
        });
  }

  handleResponse(isSuccess: boolean) {
    if (isSuccess) {
      Swal.fire({
        title: 'Your Account has been updated',
        text: 'Please login to continue',
        icon: 'success',
        confirmButtonColor: '#00ACB1',
      }).then(() => {
        this.onLogout();
        this.router.navigate(['authenticate/login']);
      });
    } else {
      Swal.fire({
        title: 'An error occurred',
        html: 'Please contact us at: <b>info@go-checks.com</b> or via the contact form',
        icon: 'error',
        confirmButtonColor: '#f37b7b',
      });
    }
  }
  
  onLogout() {
    this.authenticationService.logout();
  }
}
